import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { api } from '../utils/api';
import './AdminCalendar.css';

// ── Helpers ───────────────────────────────────────────────────
function getDaysInMonth(y, m) { return new Date(y, m + 1, 0).getDate(); }
function getFirstDayOfMonth(y, m) { return (new Date(y, m, 1).getDay() + 6) % 7; }

const MONTHS = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
const DAYS = ['Lun','Mar','Mié','Jue','Vie','Sáb','Dom'];

function getEventColor(ev) {
  if (ev.category === 'school') return 'school';
  if (ev.registrationType === 'group') return 'group';
  return 'individual';
}

// ── Modal detalle del día ─────────────────────────────────────
function DayModal({ day, month, year, events, bookings, onClose }) {
  const eventsOnDay = events.filter(ev => {
    if (!ev.date) return false;
    const s = new Date(ev.date), e = ev.dateEnd ? new Date(ev.dateEnd) : s;
    const start = new Date(s.getFullYear(), s.getMonth(), s.getDate());
    const end = new Date(e.getFullYear(), e.getMonth(), e.getDate());
    const d = new Date(year, month, day);
    return d >= start && d <= end;
  });

  return (
    <div className="cal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="cal-modal">
        <div className="cal-modal-header">
          <div>
            <span className="cal-modal-label">Detalle del día</span>
            <h2 className="cal-modal-title">{day} de {MONTHS[month]} {year}</h2>
          </div>
          <button className="cal-modal-close" onClick={onClose}>✕</button>
        </div>

        {eventsOnDay.length === 0 ? (
          <p className="cal-modal-empty">No hay eventos este día.</p>
        ) : eventsOnDay.map(ev => {
          const evB = bookings.filter(b => b.eventId === ev.id && b.status !== 'cancelled');
          const withData = evB.reduce((s, b) => s + (b.totalMembers || 0), 0);
          const ext = ev.externalCount || 0;
          const total = withData + ext;
          const cap = ev.maxCapacity || 0;
          const pct = cap > 0 ? Math.min(100, (total / cap) * 100) : 0;
          const type = getEventColor(ev);

          return (
            <div key={ev.id} className={`cal-modal-event cal-modal-event--${type}`}>
              <div className="cal-modal-event-header">
                <span className="cal-modal-event-name">{ev.name}</span>
                {ev.category === 'school' && <span className="cal-modal-event-tag">🎒 Colegio</span>}
              </div>

              <div className="cal-modal-stats">
                <div className="cal-modal-stat">
                  <span className="cal-modal-stat-val cal-modal-stat--green">{withData}</span>
                  <span className="cal-modal-stat-lbl">Con datos</span>
                </div>
                <div className="cal-modal-stat">
                  <span className="cal-modal-stat-val cal-modal-stat--amber">{ext}</span>
                  <span className="cal-modal-stat-lbl">Sin datos</span>
                </div>
                <div className="cal-modal-stat">
                  <span className="cal-modal-stat-val">{total}</span>
                  <span className="cal-modal-stat-lbl">Total</span>
                </div>
                <div className="cal-modal-stat">
                  <span className="cal-modal-stat-val cal-modal-stat--dim">{cap || '∞'}</span>
                  <span className="cal-modal-stat-lbl">Capacidad</span>
                </div>
              </div>

              {cap > 0 && (
                <div className="cal-modal-bar-wrap">
                  <div className={`cal-modal-bar ${pct >= 90 ? 'bar--red' : pct >= 70 ? 'bar--amber' : 'bar--green'}`} style={{ width: `${pct}%` }} />
                </div>
              )}
              {cap > 0 && (
                <div className="cal-modal-bar-label">{pct.toFixed(0)}% ocupado · {Math.max(0, cap - total)} libres</div>
              )}

              {evB.length > 0 && (
                <div className="cal-modal-bookings">
                  <div className="cal-modal-bookings-title">Reservas ({evB.length})</div>
                  {evB.map((b, i) => (
                    <div key={b.id || i} className="cal-modal-booking-row">
                      <span>{b.familyName || b.contactEmail || 'Sin nombre'}{b.adminCreated && <span className="cal-manual-tag">✎</span>}</span>
                      <span className="cal-modal-booking-count">{b.totalMembers || b.members?.length || 1} pers.</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        <div className="cal-modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>Cerrar</button>
        </div>
      </div>
    </div>
  );
}

// ── Página principal ──────────────────────────────────────────
export default function AdminCalendar() {
  const { token } = useAuth();
  const [events, setEvents] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth());
  const [selectedDay, setSelectedDay] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([api.getAdminEvents(token), api.getAdminBookings(token)])
      .then(([ed, bd]) => { setEvents(ed.events || []); setBookings(bd.bookings || []); })
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(load, [load]);

  const prev = () => { if (month === 0) { setYear(y => y-1); setMonth(11); } else setMonth(m => m-1); };
  const next = () => { if (month === 11) { setYear(y => y+1); setMonth(0); } else setMonth(m => m+1); };
  const today = () => { setYear(now.getFullYear()); setMonth(now.getMonth()); };

  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);

  const dayData = useMemo(() => {
    const data = {};
    for (let d = 1; d <= daysInMonth; d++) {
      const date = new Date(year, month, d);
      const dayEvts = events.filter(ev => {
        if (!ev.date) return false;
        const s = new Date(ev.date), e = ev.dateEnd ? new Date(ev.dateEnd) : s;
        return date >= new Date(s.getFullYear(), s.getMonth(), s.getDate()) &&
               date <= new Date(e.getFullYear(), e.getMonth(), e.getDate());
      });
      let personas = 0, capacidad = 0;
      dayEvts.forEach(ev => {
        const evB = bookings.filter(b => b.eventId === ev.id && b.status !== 'cancelled');
        personas += evB.reduce((s, b) => s + (b.totalMembers || 0), 0) + (ev.externalCount || 0);
        capacidad += ev.maxCapacity || 0;
      });
      data[d] = { events: dayEvts, personas, capacidad };
    }
    return data;
  }, [events, bookings, year, month, daysInMonth]);

  const monthStats = useMemo(() => {
    const ids = new Set();
    let personas = 0, activeDays = 0;
    for (let d = 1; d <= daysInMonth; d++) {
      const dd = dayData[d];
      if (dd.events.length > 0) { activeDays++; dd.events.forEach(ev => ids.add(ev.id)); }
      personas += dd.personas;
    }
    return { events: ids.size, personas, activeDays };
  }, [dayData, daysInMonth]);

  if (loading) return (
    <div className="main-content page-enter">
      <div className="loading-screen" style={{ minHeight: '50vh' }}><div className="spinner spinner-lg" /></div>
    </div>
  );

  return (
    <div className="main-content page-enter">
      <div className="cal-page-header">
        <div>
          <div className="section-label">Administración</div>
          <h1>Calendario</h1>
        </div>
      </div>

      {error && <div className="alert alert-error" style={{ marginBottom: '1rem' }}><span>⚠</span> {error}</div>}

      {/* Stats */}
      <div className="cal-stats-row">
        <div className="cal-stat-card">
          <div className="cal-stat-icon">🏕</div>
          <div><div className="cal-stat-num">{monthStats.events}</div><div className="cal-stat-label">Eventos</div></div>
        </div>
        <div className="cal-stat-card">
          <div className="cal-stat-icon">📅</div>
          <div><div className="cal-stat-num cal-stat-num--amber">{monthStats.activeDays}</div><div className="cal-stat-label">Días con actividad</div></div>
        </div>
        <div className="cal-stat-card">
          <div className="cal-stat-icon">👥</div>
          <div><div className="cal-stat-num cal-stat-num--sky">{monthStats.personas}</div><div className="cal-stat-label">Personas (total días)</div></div>
        </div>
      </div>

      {/* Calendar */}
      <div className="cal-card">
        {/* Nav */}
        <div className="cal-nav">
          <button className="btn btn-ghost btn-sm" onClick={prev}>← Anterior</button>
          <div className="cal-nav-center">
            <h2 className="cal-month-title">{MONTHS[month]} {year}</h2>
            <button className="btn btn-ghost btn-sm cal-today-btn" onClick={today}>Hoy</button>
          </div>
          <button className="btn btn-ghost btn-sm" onClick={next}>Siguiente →</button>
        </div>

        {/* Grid */}
        <div className="cal-grid">
          {/* Headers */}
          {DAYS.map(d => <div key={d} className="cal-day-header">{d}</div>)}

          {/* Empty */}
          {Array.from({ length: firstDay }).map((_, i) => <div key={`e${i}`} className="cal-cell cal-cell--empty" />)}

          {/* Days */}
          {Array.from({ length: daysInMonth }).map((_, i) => {
            const day = i + 1;
            const dd = dayData[day];
            const isToday = now.getDate() === day && now.getMonth() === month && now.getFullYear() === year;
            const hasEvents = dd.events.length > 0;
            const pct = dd.capacidad > 0 ? (dd.personas / dd.capacidad) * 100 : 0;
            const isWeekend = ((firstDay + i) % 7) >= 5;

            return (
              <div
                key={day}
                className={`cal-cell ${isToday ? 'cal-cell--today' : ''} ${hasEvents ? 'cal-cell--active' : ''} ${isWeekend ? 'cal-cell--weekend' : ''}`}
                onClick={() => hasEvents && setSelectedDay(day)}
              >
                <div className="cal-cell-top">
                  <span className={`cal-day-num ${isToday ? 'cal-day-num--today' : ''}`}>{day}</span>
                  {dd.personas > 0 && (
                    <span className={`cal-badge ${pct >= 90 ? 'cal-badge--red' : pct >= 70 ? 'cal-badge--amber' : 'cal-badge--green'}`}>
                      {dd.personas}
                    </span>
                  )}
                </div>

                <div className="cal-cell-events">
                  {dd.events.map(ev => {
                    const type = getEventColor(ev);
                    const evB = bookings.filter(b => b.eventId === ev.id && b.status !== 'cancelled');
                    const count = evB.reduce((s, b) => s + (b.totalMembers || 0), 0) + (ev.externalCount || 0);
                    return (
                      <div key={ev.id} className={`cal-event-pill cal-event-pill--${type}`} title={`${ev.name} · ${count}/${ev.maxCapacity}`}>
                        <span className="cal-event-name">{ev.name}</span>
                        <span className="cal-event-count">{count}/{ev.maxCapacity}</span>
                      </div>
                    );
                  })}
                </div>

                {hasEvents && dd.capacidad > 0 && (
                  <div className="cal-cell-bar">
                    <div className={`cal-cell-bar-fill ${pct >= 90 ? 'bar--red' : pct >= 70 ? 'bar--amber' : 'bar--green'}`} style={{ width: `${Math.min(100, pct)}%` }} />
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Legend */}
        <div className="cal-legend">
          <div className="cal-legend-left">
            <div className="cal-legend-item"><div className="cal-legend-dot cal-legend-dot--individual" /> Individual</div>
            <div className="cal-legend-item"><div className="cal-legend-dot cal-legend-dot--group" /> Grupal</div>
            <div className="cal-legend-item"><div className="cal-legend-dot cal-legend-dot--school" /> Colegio</div>
          </div>
          <div className="cal-legend-right">
            <span><span className="cal-legend-sq cal-legend-sq--green" /> &lt;70%</span>
            <span><span className="cal-legend-sq cal-legend-sq--amber" /> 70-90%</span>
            <span><span className="cal-legend-sq cal-legend-sq--red" /> &gt;90%</span>
          </div>
        </div>
      </div>

      {selectedDay && (
        <DayModal day={selectedDay} month={month} year={year} events={events} bookings={bookings} onClose={() => setSelectedDay(null)} />
      )}
    </div>
  );
}
